import { useEffect, useRef, useState } from 'react';

type AsyncActionState = 'idle' | 'loading' | 'success' | 'error';

interface UseAsyncActionOptions {
  onClick: () => Promise<void>;
  onError?: (error: unknown) => void;
  resetDelay?: number;
}

const useAsyncAction = ({ onClick, onError, resetDelay = 3000 }: UseAsyncActionOptions) => {
  const [state, setState] = useState<AsyncActionState>('idle');
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleClick = async () => {
    setState('loading');
    try {
      await onClick();
      setState('success');
    } catch (error) {
      setState('error');
      onError?.(error);
      throw error;
    }
  };

  useEffect(() => {
    if (state === 'success' || state === 'error') {
      resetTimerRef.current = setTimeout(() => {
        setState('idle');
      }, resetDelay);
    }
    return () => {
      if (resetTimerRef.current) {
        clearTimeout(resetTimerRef.current);
        resetTimerRef.current = null;
      }
    };
  }, [state, resetDelay]);

  return { state, handleClick };
};

export default useAsyncAction;
